import { FC, useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, Typography, IconButton, Tooltip, FormHelperText } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useForm, useSnackbar, useDrawer } from '@carcareers/contexts';
import { useResume } from '@carcareers/hooks';
import { JobseekerSelect } from '@carcareers/components';

interface ResumeFormProps {
  onSuccess?: () => void;
}

export const ResumeForm: FC<ResumeFormProps> = ({ onSuccess }) => {
  const [files, setFiles] = useState<File[]>([]);
  const { setOpen } = useDrawer();
  const { toggleSnackbar } = useSnackbar();
  const { createResume } = useResume();
  const { data } = useForm();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (files.length === 0) {
      toggleSnackbar('error', 'Please upload at least one resume file');
      return;
    }

    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append('resumes', file);
      });

      if (data?.jobseekerId && files.length === 1) {
        const id = typeof data.jobseekerId === 'object' && data.jobseekerId?.id 
          ? data.jobseekerId.id 
          : data.jobseekerId;
          
        formData.append('jobseekerId', id.toString());
      }

      const callbacks = onSuccess ? { onSuccess } : undefined;
      const response = await createResume(formData, callbacks);

      if (response?.data) {
        setFiles([]);
        setOpen(false);
      }
    } catch (error) {
      toggleSnackbar('error', 'Failed to upload resume');
      console.error('Upload error:', error);
    }
  };

  const isMultipleFiles = files.length > 1;

  useEffect(() => {
    return () => {
      setOpen(false);
    };
  }, [setOpen]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      encType="multipart/form-data"
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box>
        <JobseekerSelect name="jobseekerId" disabled={isMultipleFiles} />
        {isMultipleFiles && (
          <FormHelperText sx={{ color: 'warning.main', mt: 1 }}>
            You can only assign a jobseeker to a single resume. The jobseeker will not be included with these uploads.
          </FormHelperText>
        )}
      </Box>

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        <Typography>
          {isDragActive ? 'Drop the files here' : 'Drag and drop resume files here, or click to select files'}
        </Typography>
      </Box>

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected files:
          </Typography>
          {files.map((file, index) => (
            <Box
              key={index}
              sx={{
                py: 1,
                pl: 2,
                pr: 1,
                mb: 1,
                display: 'flex',
                borderRadius: 2,
                alignItems: 'center',
                transition: 'all 100ms',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              <Typography sx={{ flex: 1 }}>
                {file.name.length > 42 ? `${file.name.substring(0, 42)}...` : file.name}
              </Typography>
              <Tooltip title="Remove file" placement="top">
                <IconButton size="small" onClick={() => removeFile(index)}>
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Button onClick={handleSubmit} variant="contained" disabled={files.length === 0} fullWidth>
        Upload Resume
      </Button>
    </Box>
  );
};
